import { useMemo, useState } from 'react';
import { useBulkResendOrders } from '@/hooksApi/useInventory';
import { useBulkRequestRto } from '@/hooksApi/useOrder';
import { BulkActionResponse } from '@/types/order';

export type BulkAction = 'resend' | 'rto';

/** What the bulk bar needs to know about a selected row. */
export interface BulkSourceRow {
  key: string;
  platform_code: string;
  status: { slug: string };
  merchant_action_required?: number | boolean;
}

/** One order in the dialog: its code and, when it is not going, why (an i18n key or the server's text). */
export interface BulkRow {
  uid: string;
  code: string;
  reason?: string;
}

export interface BulkPlan {
  eligible: BulkRow[];
  skipped: BulkRow[];
}

export interface BulkOutcome {
  done: BulkRow[];
  refused: BulkRow[];
}

// The same rules the server applies (ResendOrderJob / the request-RTO door).
// Only a preview: the server's answer is what the result view shows.
const RESENDABLE = ['inventory', 'exception'];

const qualifies = (row: BulkSourceRow, action: BulkAction): boolean =>
  action === 'resend'
    ? RESENDABLE.includes(row.status?.slug)
    : row.status?.slug === 'on-hold' && !!row.merchant_action_required;

export const planBulkAction = (rows: BulkSourceRow[], action: BulkAction): BulkPlan => {
  const plan: BulkPlan = { eligible: [], skipped: [] };

  rows.forEach((row) => {
    const item = { uid: row.key, code: row.platform_code };
    if (qualifies(row, action)) {
      plan.eligible.push(item);
    } else {
      plan.skipped.push({
        ...item,
        reason: action === 'resend' ? 'bulk_skip_not_resendable' : 'bulk_skip_not_waiting',
      });
    }
  });

  return plan;
};

/** Map the server's results/errors back to platform codes. */
export const toBulkOutcome = (
  response: BulkActionResponse | undefined,
  plan: BulkPlan,
): BulkOutcome => {
  const codeOf = (uid: string, fallback?: string) =>
    plan.eligible.find((row) => row.uid === uid)?.code ?? fallback ?? uid;

  return {
    done: (response?.object?.results ?? []).map((row) => ({
      uid: row.uid,
      code: codeOf(row.uid, row.platform_code),
    })),
    refused: (response?.object?.errors ?? []).map((row) => ({
      uid: row.uid,
      code: codeOf(row.uid, row.platform_code),
      reason: row.error,
    })),
  };
};

/**
 * BOXY-376 — bulk Resend / Request RTO from the orders list selection.
 * Counts come from the rows' statuses; only the qualifying orders are sent,
 * and the result view shows the server's per-order answer.
 */
const useBulkOrderActions = (rows: BulkSourceRow[]) => {
  const [action, setAction] = useState<BulkAction | null>(null);
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);
  const [failed, setFailed] = useState(false);

  const resendPlan = useMemo(() => planBulkAction(rows, 'resend'), [rows]);
  const rtoPlan = useMemo(() => planBulkAction(rows, 'rto'), [rows]);
  const plan = action === 'rto' ? rtoPlan : resendPlan;

  const { mutate: bulkResend, isLoading: resending } = useBulkResendOrders();
  const { mutate: bulkRequestRto, isLoading: requesting } = useBulkRequestRto();

  const open = (next: BulkAction) => {
    setAction(next);
    setNote('');
    setOutcome(null);
    setFailed(false);
  };

  const close = () => setAction(null);

  const submit = () => {
    const uids = plan.eligible.map((row) => row.uid);
    const handlers = {
      onSuccess: (response: BulkActionResponse) => setOutcome(toBulkOutcome(response, plan)),
      onError: () => setFailed(true),
    };

    if (action === 'resend') {
      bulkResend({ orders: uids.map((uid) => ({ uid })) }, handlers);
    } else {
      bulkRequestRto(
        { orders_uid: uids, merchant_action_required_merchant_text: note.trim() || undefined },
        handlers,
      );
    }
  };

  return {
    action,
    plan,
    resendCount: resendPlan.eligible.length,
    rtoCount: rtoPlan.eligible.length,
    note,
    setNote,
    outcome,
    failed,
    isSubmitting: resending || requesting,
    open,
    close,
    submit,
  };
};

export default useBulkOrderActions;
